import { toast } from 'react-toastify';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

export interface GraphQLError {
  message: string;
  code?: string | null;
  field?: string;
  path?: string[];
  extensions?: Record<string, any>;
}

export interface MutationPayload<T = unknown> {
  data?: T;
  errors?: GraphQLError[] | null;
}

export interface FieldError {
  field: string;
  message: string;
  code?: string;
}

/**
 * Extract error messages from a GraphQL mutation payload
 */
export const extractErrorMessages = (payload: MutationPayload): string[] => {
  if (!payload.errors || payload.errors.length === 0) {
    return [];
  }

  return payload.errors.map((error) => {
    if (error.field) {
      return `${error.field}: ${error.message}`;
    }
    return error.message;
  });
};

/**
 * Extract field-specific errors from a GraphQL mutation payload
 */
export const extractFieldErrors = (payload: MutationPayload): Record<string, string> => {
  const fieldErrors: Record<string, string> = {};
  
  if (!payload.errors || payload.errors.length === 0) {
    return fieldErrors;
  }
  
  payload.errors.forEach((error) => {
    if (error.field) {
      fieldErrors[error.field] = error.message;
    } else if (error.path && error.path.length > 0) {
      // Handle nested field paths
      const fieldName = error.path.join('.');
      fieldErrors[fieldName] = error.message;
    }
  });
  
  return fieldErrors;
};

/**
 * Parse RTK Query error to extract meaningful error messages
 */
export const parseRtkQueryError = (
  error: FetchBaseQueryError | SerializedError | undefined
): string[] => {
  if (!error) return [];
  
  // Handle FetchBaseQueryError
  if ('status' in error) {
    if (error.data) {
      // GraphQL errors
      if (typeof error.data === 'object' && 'errors' in error.data) {
        const graphqlErrors = error.data.errors as GraphQLError[];
        return graphqlErrors.map(e => e.field ? `${e.field}: ${e.message}` : e.message);
      }
      // Simple error message
      if (typeof error.data === 'object' && 'message' in error.data) {
        return [error.data.message as string];
      }
      // String error
      if (typeof error.data === 'string') {
        return [error.data];
      }
    }
    // HTTP status errors
    if (error.status === 401) return ['Unauthorized. Please log in again.'];
    if (error.status === 403) return ['You do not have permission to perform this action.'];
    if (error.status === 404) return ['The requested resource was not found.'];
    if (error.status === 500) return ['Internal server error. Please try again later.'];
    return [`Request failed with status ${error.status}`];
  }
  
  // Handle SerializedError
  if ('message' in error && error.message) {
    return [error.message];
  }
  
  return ['An unexpected error occurred'];
};

/**
 * Show error messages from a GraphQL mutation payload
 */
export const showMutationErrors = (payload: MutationPayload): void => {
  const messages = extractErrorMessages(payload);
  
  if (messages.length === 0) {
    return;
  }

  if (messages.length === 1) {
    toast.error(messages[0]);
  } else {
    // Show multiple errors in a single toast
    const errorMessage = `Multiple errors occurred:\n${messages.map((msg, _index) => `• ${msg}`).join('\n')}`;
    toast.error(errorMessage);
  }
};

/**
 * Handle a successful mutation
 */
export const handleMutationSuccess = (message: string): void => {
  toast.success(message);
};

/**
 * Check if a mutation was successful (no errors)
 */
export const isMutationSuccessful = (payload: MutationPayload): boolean => {
  return !payload.errors || payload.errors.length === 0;
};

export const mutationPayloadError = (payload: MutationPayload): Error =>
  new Error(payload.errors?.[0]?.message ?? 'The operation was rejected by the server.');

export const transportErrorMessage = (error: unknown, fallback = 'Request failed.'): string =>
  typeof error === 'object' && error !== null && 'message' in error
    ? String((error as { message: unknown }).message)
    : fallback;

/**
 * Generic error handler for RTK Query errors
 */
export const handleApiError = (
  error: FetchBaseQueryError | SerializedError | undefined
): void => {
  const messages = parseRtkQueryError(error);
  
  if (messages.length === 0) {
    toast.error('An unexpected error occurred');
    return;
  }
  
  if (messages.length === 1) {
    toast.error(messages[0]);
  } else {
    const errorMessage = `Multiple errors occurred:\n${messages.map(msg => `• ${msg}`).join('\n')}`;
    toast.error(errorMessage);
  }
};

/**
 * Create a retry handler for failed requests
 */
export const createRetryHandler = (
  action: () => Promise<any>,
  maxRetries: number = 3,
  delay: number = 1000
) => {
  return async (): Promise<any> => {
    let lastError: any;
    
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await action();
      } catch (error) {
        lastError = error;
        if (i < maxRetries - 1) {
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));
        }
      }
    }
    
    throw lastError;
  };
};

/**
 * Show field-specific errors in a form
 */
export const showFieldErrors = (
  errors: Record<string, string>,
  setFieldError: (field: string, message: string) => void
): void => {
  Object.entries(errors).forEach(([field, message]) => {
    setFieldError(field, message);
  });
};